import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Search, Pencil, Trash2, Download, ChevronUp, ChevronDown, Merge } from "lucide-react";

import { PhotographerDialog } from "./photographers/PhotographerDialog";
import { ImportPhotographersButton } from "./photographers/ImportPhotographersButton";
import { ImportPhotographersHelp } from "./photographers/ImportPhotographersHelp";
import { MergeDuplicatesModal } from "./photographers/MergeDuplicatesModal";

export default function Photographers() {
  const { user } = useAuth();
  const canEdit = ["ADMIN", "SECRETARY"].includes(user?.role);
  const canDelete = user?.role === "ADMIN";

  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  // Bulk-delete selection. Kept as a Set for O(1) toggling.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Default: sort by photographer_code (I.D.) ascending. Click a header to
  // toggle direction; click the OTHER header to switch column.
  const [sortKey, setSortKey] = useState("photographer_code");
  const [sortDir, setSortDir] = useState("asc");

  const sortedList = useMemo(() => {
    const rows = [...list];
    const dir = sortDir === "asc" ? 1 : -1;
    // Numeric-aware comparator so "10" sorts after "2" rather than before
    // it — important for photographer codes which often start with digits.
    const cmp = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" }).compare;
    rows.sort((a, b) => dir * cmp(a[sortKey] || "", b[sortKey] || ""));
    return rows;
  }, [list, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // Renders the chevron next to a sortable column header. We don't make a
  // sub-component for the whole <th> because eslint flags components defined
  // inside the parent (they'd get a fresh identity each render).
  const sortIndicator = (k) =>
    sortKey === k &&
    (sortDir === "asc" ? (
      <ChevronUp size={12} aria-hidden="true" className="inline ml-1" />
    ) : (
      <ChevronDown size={12} aria-hidden="true" className="inline ml-1" />
    ));

  const load = async () => {
    const { data } = await api.get("/photographers", { params: q ? { q } : {} });
    setList(data);
  };

  useEffect(() => {
    const t = setTimeout(load, q ? 200 : 0);
    return () => clearTimeout(t);
  }, [q]);

  const startCreate = () => {
    setEditing({
      photographer_code: "",
      first_name: "",
      last_name: "",
      display_name: "",
      membership_number: "",
      email: "",
      tel_no: "",
      active_status: true,
      enters_competitions: true,
    });
    setOpen(true);
  };

  const startEdit = (p) => {
    setEditing({ ...p });
    setOpen(true);
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.display_name}?`)) return;
    await api.delete(`/photographers/${p.id}`);
    load();
  };

  // --- Bulk delete ---
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allVisibleSelected =
    sortedList.length > 0 && sortedList.every((p) => selected.has(p.id));

  const toggleSelectAll = () =>
    setSelected((prev) => {
      if (allVisibleSelected) {
        // Deselect the visible slice only — preserves selection from
        // filtered-away rows if the user then clears the search.
        const next = new Set(prev);
        sortedList.forEach((p) => next.delete(p.id));
        return next;
      }
      const next = new Set(prev);
      sortedList.forEach((p) => next.add(p.id));
      return next;
    });

  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(
      `Delete ${ids.length} photographer${ids.length === 1 ? "" : "s"}?\n\n` +
      "Any scores they have entered will remain in reports as historical data."
    )) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/photographers/bulk-delete", { ids });
      const parts = [`${data.deleted} deleted`];
      if (data.orphaned_entries) parts.push(`${data.orphaned_entries} score${data.orphaned_entries === 1 ? "" : "s"} kept for history`);
      if (data.not_found) parts.push(`${data.not_found} not found`);
      toast.success(parts.join(" · "));
      clearSelection();
      await load();
    } catch (ex) {
      toast.error(`Bulk delete failed: ${ex?.response?.data?.detail || ex.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleEnters = async (p) => {
    const next = !(p.enters_competitions !== false);
    // Optimistic UI — flip locally first so the click feels instant.
    setList((rows) => rows.map((r) => (r.id === p.id ? { ...r, enters_competitions: next } : r)));
    try {
      await api.patch(`/photographers/${p.id}`, { ...p, enters_competitions: next });
      toast.success(
        next
          ? `${p.display_name} will now be included by "Add all photographers"`
          : `${p.display_name} will be skipped by "Add all photographers"`
      );
    } catch (ex) {
      // Roll back on failure.
      setList((rows) => rows.map((r) => (r.id === p.id ? { ...r, enters_competitions: !next } : r)));
      toast.error(`Couldn't update ${p.display_name}: ${ex?.response?.data?.detail || ex.message}`);
    }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("ccs_token");
    // Append a timestamp so neither the browser nor any intermediate proxy
    // returns a cached CSV from before the operator edited photographers.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/photographers.csv?t=${Date.now()}`;
    const r = await fetch(url, {
      cache: "no-store",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "photographers.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="ccs-label mb-2">Database</div>
          <h1 className="text-4xl font-semibold tracking-tight">Photographers</h1>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMergeOpen(true)}
              className="ccs-btn-ghost flex items-center gap-2 text-sm"
              data-testid="merge-duplicates-button"
              title="Find and merge duplicate photographers"
            >
              <Merge size={14} /> Merge duplicates
            </button>
            <button
              onClick={exportCsv}
              className="ccs-btn-ghost flex items-center gap-2 text-sm"
              data-testid="export-photographers-button"
              title="Download all photographers as a CSV"
            >
              <Download size={14} /> Export CSV
            </button>
            <ImportPhotographersButton onImported={load} />
            <ImportPhotographersHelp />
            <button onClick={startCreate} className="ccs-btn" data-testid="add-photographer-button">
              <Plus size={16} /> New photographer
            </button>
          </div>
        )}
      </div>

      <div className="relative mb-5 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <input
          className="ccs-input pl-10"
          placeholder="Search I.D. or name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="photographer-search"
        />
      </div>

      {canDelete && selected.size > 0 && (
        <div
          className="mb-4 flex items-center justify-between gap-3 p-3 text-sm"
          style={{ background: "#2a1200", border: "1px solid #7a2f00" }}
          data-testid="bulk-delete-bar"
        >
          <div>
            <b>{selected.size}</b> photographer{selected.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearSelection}
              className="ccs-btn-ghost text-xs"
              data-testid="bulk-clear-button"
            >
              Clear
            </button>
            <button
              onClick={bulkDelete}
              disabled={bulkBusy}
              className="ccs-btn text-xs flex items-center gap-2"
              style={{ background: "#7a2f00", borderColor: "#a04010" }}
              data-testid="bulk-delete-button"
            >
              <Trash2 size={13} />
              {bulkBusy ? "Deleting…" : `Delete ${selected.size}`}
            </button>
          </div>
        </div>
      )}

      <div className="ccs-card p-0 overflow-x-auto">
        <table className="ccs-table" data-testid="photographers-table">
          <thead>
            <tr>
              {canDelete && (
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all photographers"
                    className="accent-amber-400 cursor-pointer"
                    data-testid="bulk-select-all"
                  />
                </th>
              )}
              <th
                onClick={() => toggleSort("photographer_code")}
                className="cursor-pointer select-none hover:text-amber-400"
                data-testid="sort-by-photographer_code"
                aria-sort={sortKey === "photographer_code" ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
              >
                I.D.{sortIndicator("photographer_code")}
              </th>
              <th
                onClick={() => toggleSort("display_name")}
                className="cursor-pointer select-none hover:text-amber-400"
                data-testid="sort-by-display_name"
                aria-sort={sortKey === "display_name" ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
              >
                Name{sortIndicator("display_name")}
              </th>
              <th>Membership</th>
              <th>Email</th>
              <th>Tel No.</th>
              <th>Status</th>
              <th title="Tick if this photographer enters club competitions. The Live Entry 'Add all photographers' button only adds ticked members.">Enters</th>
              <th style={{ width: 120 }}></th>
            </tr>
          </thead>
          <tbody>
            {sortedList.map((p) => (
              <tr key={p.id} data-testid={`photographer-row-${p.photographer_code}`}>
                {canDelete && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      aria-label={`Select ${p.display_name}`}
                      className="accent-amber-400 cursor-pointer"
                      data-testid={`bulk-select-${p.photographer_code}`}
                    />
                  </td>
                )}
                <td className="mono text-amber-400 font-medium">{p.photographer_code}</td>
                <td>{p.display_name}</td>
                <td className="mono muted-text">{p.membership_number || "—"}</td>
                <td className="muted-text">{p.email || "—"}</td>
                <td className="mono muted-text">{p.tel_no || "—"}</td>
                <td>
                  {p.active_status ? (
                    <span className="active-pill">Active</span>
                  ) : (
                    <span className="tag">Inactive</span>
                  )}
                </td>
                <td className="text-center">
                  <input
                    type="checkbox"
                    checked={p.enters_competitions !== false}
                    onChange={() => toggleEnters(p)}
                    disabled={!canEdit}
                    className="accent-amber-400 cursor-pointer"
                    title={p.enters_competitions !== false
                      ? "Included by 'Add all photographers'"
                      : "Skipped by 'Add all photographers' — still addable individually"}
                    data-testid={`enters-toggle-${p.photographer_code}`}
                  />
                </td>
                <td className="text-right">
                  {canEdit && (
                    <button
                      onClick={() => startEdit(p)}
                      className="p-2 hover:bg-neutral-800"
                      title="Edit"
                      data-testid={`edit-photographer-${p.photographer_code}`}
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => remove(p)}
                      className="p-2 hover:bg-neutral-800 text-red-400"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {sortedList.length === 0 && (
              <tr>
                <td colSpan={canDelete ? 9 : 8} className="text-center py-14" data-testid="photographers-empty">
                  <div className="text-4xl mb-2">📸</div>
                  <div className="text-white text-lg font-medium mb-1">
                    {q ? "No matches" : "No photographers yet"}
                  </div>
                  <div className="muted-text text-sm mb-4">
                    {q
                      ? `Nothing matches "${q}". Try clearing the search.`
                      : "Add your club members to start recording their scores."}
                  </div>
                  {!q && (
                    <button
                      className="ccs-btn inline-flex items-center gap-2"
                      onClick={startCreate}
                    >
                      <Plus size={14} /> Add first photographer
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && editing && (
        <PhotographerDialog
          initial={editing}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            load();
          }}
        />
      )}
      {mergeOpen && (
        <MergeDuplicatesModal
          onClose={() => setMergeOpen(false)}
          onMerged={load}
        />
      )}
    </div>
  );
}
